import useOverlay from "@/hooks/common/useOverlay";
import {useTaggingClick} from "@/hooks/common/useTagging";
import {getQueryParams, getTarget, hrefSSO} from "@COMPONENTS/common/commonUtil";
import ErrorBoundary from "@COMPONENTS/common/error/ErrorBoundary";
import {getPrice, isGoodsTpFH, reserveButtonText} from "@COMPONENTS/common/eventUtil";
import Alert from "@COMPONENTS/common/modal/alert/Mobile";
import SSOLink from "@COMPONENTS/common/ssoLink";
import CouponPop, {CouponPopType} from "@COMPONENTS/product/common/popup/mobile/CouponPop";
import EventListPopup from "@COMPONENTS/product/common/popup/mobile/EventListPopup";
import InquiryPopup from "@COMPONENTS/product/pages/detailPackage/mobile/detailSNB/InquiryPopup";
import {TDetailPackage} from "@COMPONENTS/product/types";
import publicRuntimeConfig from "@FRAMEWORK/config/public";
import {CountingButton, TextDefaultButton} from "@FRAMEWORK/ui/mobile/button";
import errorImg from "@PUBLIC/M/images/ico/ico_nodata.png";
import SpriteImage from "@PUBLIC/M/images/ico/ico_spr.png";
import useCustomerMe from "@QUERIES/account/useCustomerMe";
import useBannerContents from "@QUERIES/essential/banner/useBannerContents";
import {eventAvailabilityAsync} from "@QUERIES/prdt/event/useEventAvailability";
import useEventPrice from "@QUERIES/prdt/event/useEventPrice";
import styled from "@yb/styled-px2rem";
import {format} from "@yb/utils";
import dayjs from "dayjs";
import {memo, useMemo, useState} from "react";

const Wrap = styled.div`
	position: relative;
`;

const FixedBtnBlock = styled.div`
	z-index: 6;
	display: flex;
	flex: 1 1 100%;
	width: 100%;
	position: fixed;
	left: 0;
	right: 0;
	bottom: 0;
	button[type="button"] {
		padding: 4px 16px;
		&:disabled {
			background-color: var(--disable-line-color);
		}
	}
`;

const InquiryButton = styled(TextDefaultButton)`
	min-width: 20%;
	height: 56px;
	border-radius: 0;
	background-color: #fff9e8;
	color: var(--title-color);
	font-size: var(--regular-size);

	+ button {
		margin-left: 0;
	}
`;
const ChangeDepartureButton = styled(TextDefaultButton)`
	min-width: 30%;
	height: 56px;
	border-radius: 0;
	background-color: var(--title-color);
	font-size: var(--regular-size);
	color: #fff;
	+ button {
		margin-left: 0;
	}
`;
const ReserveButton = styled(TextDefaultButton)<{waiting?: boolean; isDisabled?: boolean}>`
	min-width: 50%;
	height: 56px;
	border-radius: 0 !important;
	background-color: var(--brand-color);
	font-family: "PT-bold";
	font-size: 20px;
	/* color: ${({waiting}) => (waiting ? "#fff" : "var(--title-color)")}; */
	color: var(--title-color);

	${({isDisabled}) =>
		isDisabled
			? `
		background-color: var(--disable-text-color);
		color: #fff;
		cursor: unset;`
			: ``}
`;

const LayerWrap = styled.div`
	position: relative;
	bottom: -20px;
	z-index: 101;
	width: 100%;
	padding: 36px 0 0;
	border-top-left-radius: 15px;
	border-top-right-radius: 15px;
	background: #fff;
	box-shadow: 0 0 4px rgba(0, 0, 0, 0.25);
`;

const PartnerLayerWrap = styled(LayerWrap)`
	position: fixed;
	bottom: 55px;
	left: 0;
	right: 0;
	box-shadow: 0 0 4px rgba(0, 0, 0, 0.25);
`;

const InfoList = styled.ul`
	/* margin: 10px 0; */
	padding: 0 20px;

	@media (orientation: landscape) {
		max-height: 50px;
		overflow-y: scroll;
	}
`;

const InfoListItem = styled.li`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 15px;
	+ li {
		padding-top: 16px;
	}
	> div {
		p {
			line-height: 1;
			margin-bottom: 4px;
			font-family: "PT-medium";
			font-size: var(--basic-size);
			color: var(--sub-title-color);
		}
	}
`;

const ItemPrice = styled.span`
	width: 100%;
	line-height: 1;
	text-align: right;
	font-size: var(--regular-size);
	font-family: "PT-semibold";
	color: var(--title-color);
	> em {
		font-family: "PT-bold";
	}
`;

const ImgWrap = styled.div`
	width: 100%;
	height: 50px;
	padding: 0 20px;

	img {
		width: 100%;
		height: 100%;
	}
	+ h4 {
		margin-top: 16px;
	}
`;

const DayTitle = styled.h4`
	line-height: 1.5;
	padding: 0 0 16px;
	margin: 0 20px 16px;
	border-bottom: 1px solid var(--hr-color);
	font-size: var(--regular-size);
	font-family: "PT-semibold";
	color: var(--title-color);
`;

const TotalWrap = styled.div<{disabled?: boolean}>`
	display: flex;
	align-items: center;
	gap: 10px;
	height: 56px;
	margin-top: 24px;
	padding: 8px 20px;
	background-color: var(--label-bg-color);

	button {
		display: flex;
		justify-content: center;
		align-items: center;
		gap: 6px;
		height: 24px;
		padding: 2px 8px;
		border-radius: 8px;
		border: 1px solid var(--brand-deep-color);
		background: #fff9e8;
		font-size: var(--small-size);
		font-family: "PT-bold";
		color: var(--brand-deep-color);
		white-space: nowrap;

		&::before {
			display: flex;
			width: 16px;
			height: 16px;
			background: url(${SpriteImage}) no-repeat 0 -560px;
			content: "";
		}
	}

	> span {
		line-height: 1.5;
		font-family: "PT-medium";
		font-size: 16px;
		color: var(--primary-color);
		> em {
			font-family: "PT-bold";
			font-size: 26px;
		}
	}

	${ItemPrice} {
		color: ${({disabled}) => (disabled ? `var(--disable-text-color)` : `var(--primary-color)`)};
	}
`;

const LayerCloseButton = styled.button`
	position: absolute;
	top: 0;
	left: 50%;
	z-index: 6;
	width: 100%;
	height: 36px;
	background: transparent;
	border-bottom: 0;
	transform: translateX(-50%);

	&::before {
		position: absolute;
		top: 17px;
		left: 50%;
		display: flex;
		width: 18px;
		height: 8px;
		background: url(${SpriteImage}) no-repeat -16px -560px;
		transform: translateX(calc(-50% 0));
		content: "";
	}
`;

const daykor = ["일", "월", "화", "수", "목", "금", "토"];

const DetailSNBComponent = (props: TDetailPackage) => {
	const maxCount = 30;
	const {eventDetail: event} = props;
	const [show, setShow] = useState(false);
	// 패밀리 상품(familyYn === "Y")은 성인 2, 아동 2, 유아 0으로 기본 세팅
	const [adult, setAdult] = useState(event.familyYn === "Y" ? 2 : 1);
	const [child, setChild] = useState(event.familyYn === "Y" ? 2 : 0);
	const [infant, setInfant] = useState(0);
	const [coupon, setCoupon] = useState<CouponPopType[]>([]);
	const [open] = useOverlay();

	const {evStartDt, evCd, goodsTp, goodsCd} = event;
	const startDtDayjs = dayjs(evStartDt);
	const startFormat = `${startDtDayjs.format("YYYY년 MM월 DD일")} (${daykor[startDtDayjs.day()]}요일 출발)`;

	/** 로그인 정보 */
	const useSign = useCustomerMe();
	const isSignin = useSign.data?.body.nonUser === false;

	/** 배너 */
	const useBnrContents = useBannerContents("GORV"); // WEB_CONT_DIVI_CD 상품상세_요금영역
	const bannerList = useBnrContents.data?.body || [];

	/** 금액 */
	const useEvtPrice = useEventPrice(evCd);
	const price = getPrice(useEvtPrice.data?.body);

	/** 쿠폰 할인금액 */
	const discount = useMemo(() => coupon.reduce((acc, cur) => acc + cur.calcObj.totalPrice, 0), [coupon]);

	/** 총 금액 */
	const totalPrice = useMemo(() => {
		if (!price) return 0;
		return price.adult * adult + price.child * child + price.infant * infant - discount;
	}, [adult, child, infant, price, discount]);

	/** 쿠폰 초기화 */
	const resetCoupon = () => {
		if (discount > 0) {
			MessageUtil.alert("쿠폰을 재적용 해주세요.");
			setCoupon([]);
		}
	};

	/** 쿠폰 팝업 */
	const openCoupon = () => {
		const callbackCoupon = (prop: CouponPopType[]) => {
			setCoupon(prop);
		};
		if (isSignin) {
			const member = {
				adult,
				child,
				infant,
			};
			open(overlay => <CouponPop {...overlay} evCd={evCd} memberCount={member} callback={callbackCoupon} />);
		} else {
			open(prop => (
				<Alert
					{...prop}
					alertType="confirm"
					text="로그인 후 이용하실 수 있습니다. <br/>로그인하시겠습니까?"
					afterConfirm={() => LoginUtil.login()}
				/>
			));
		}
	};

	/** 예약하기 버튼 */
	const reserveText = reserveButtonText(props.eventDetail);
	const handleReserve = async () => {
		if (!show) {
			let msg = "";
			if (reserveText[1] === "예약마감") {
				msg =
					"예약이 마감되었습니다.<br/>출발일 변경에서 예약가능 상품으로<br/>예약하시거나, 담당자에게 문의 해주세요.";
			} else if (reserveText[1] === "출발완료") {
				msg =
					"이미 출발한 상품입니다.<br/>출발일 변경에서 예약가능 상품으로<br/>예약하시거나, 담당자에게 문의 해주세요.";
			}
			if (msg) {
				MessageUtil.alert(msg);
				return;
			}
			setShow(true);
			return;
		}

		// 패밀리 상품은 (성인2 + 아동2)의 배수여야 예약 가능 (유아는 조건에 영향 없음)
		if (event.familyYn === "Y") {
			const isValidFamily = adult === child && adult > 0 && adult % 2 === 0;
			if (!isValidFamily) {
				MessageUtil.alert(
					"패밀리팩 상품입니다.<br/>성인과 아동 인원을 동일하게 선택해 주세요.<br/>(예: 성인 2명 + 아동 2명)",
				);
				return;
			}
		}

		if (event.fixRervCd === "YT") {
			const eventAvailability = await eventAvailabilityAsync(evCd);
			if (eventAvailability.data?.body < adult + child + infant) {
				MessageUtil.alert("예약 가능 인원을 초과하였습니다.<br/>담당자에게 문의해 주세요.");
				setShow(false);
				return;
			}
		}
		const adtCnt = adult > 1 ? `${adult}` : "1";
		const chdCnt = child > 0 ? `${child}` : "0";
		const infCnt = infant > 0 ? `${infant}` : "0";
		const menu = getQueryParams(location.search)?.menu ?? "";
		const couponParams = coupon.map(v => v.couponNo).join(",");
		const nextUrl = `${
			publicRuntimeConfig.ENV_TRAVEL_URL
		}/pkg/reserve?evCd=${evCd}&adtCnt=${adtCnt}&chdCnt=${chdCnt}&infCnt=${infCnt}&menu=${menu}${
			couponParams ? `&coupon=${couponParams}` : ""
		}`;

		/* 로그인 안되어있으면 로그인페이지 이동 */
		if (!isSignin) {
			LoginUtil.loginRsvn(nextUrl);
			return;
		}

		hrefSSO(nextUrl);
	};

	const changeDepartureDay = () => {
		open(popupProps => <EventListPopup {...popupProps} goodsCd={goodsCd} startMonth={evStartDt} />);
	};

	/** 문의하기 */
	const hanldeQna = () => {
		open(overlay => <InquiryPopup {...overlay} evCd={evCd} />);
	};

	// tagging 출발일 보기
	const {taggingClick: taggingClickChange} = useTaggingClick("pkg_select_dtl_change", event);
	// tagging 예약하기
	const {taggingClick: taggingClickReserve} = useTaggingClick("pkg_select_evCd_pkgDtl", {
		eventDetail: event,
		count: {adultCnt: adult, childCnt: child, infantCnt: infant},
		coupon,
	});

	return (
		<Wrap>
			{show && (
				<PartnerLayerWrap>
					<LayerCloseButton onClick={() => setShow(false)}>
						<span>닫기</span>
						<i />
					</LayerCloseButton>
					{bannerList.map(({mLink, mTarget, mAlt, mImgPath}, index) => (
						<ImgWrap key={index}>
							<SSOLink
								href={`${publicRuntimeConfig.ENV_PACKAGE_M_URL}${mLink}`}
								key={index}
								target={getTarget(mTarget)}
							>
								<img src={mImgPath ? `${publicRuntimeConfig.ENV_CIMG_CDN_URL}${mImgPath}` : errorImg} alt={mAlt} />
							</SSOLink>
						</ImgWrap>
					))}
					<DayTitle>{startFormat}</DayTitle>
					<InfoList>
						<InfoListItem>
							<div>
								<p>성인</p>
								<ItemPrice>
									<em>{format.koKRFormatNumber(price.adult)}</em> 원
								</ItemPrice>
							</div>
							<CountingButton
								minValue={1}
								increase={(count: number) => {
									setAdult(count);
									resetCoupon();
								}}
								decrease={(count: number) => {
									if (count > maxCount) return;
									setAdult(count);
									resetCoupon();
								}}
								value={adult}
							/>
						</InfoListItem>
						<InfoListItem>
							<div>
								<p>아동</p>
								{isGoodsTpFH(goodsTp) && price?.child === 0 ? (
									<>별도문의</>
								) : (
									<ItemPrice>
										<em>{format.koKRFormatNumber(price.child)}</em> 원
									</ItemPrice>
								)}
							</div>
							<CountingButton
								increase={(count: number) => {
									setChild(count);
									resetCoupon();
								}}
								decrease={(count: number) => {
									setChild(count);
									resetCoupon();
								}}
								value={child}
							/>
						</InfoListItem>
						<InfoListItem>
							<div>
								<p>유아</p>
								{isGoodsTpFH(goodsTp) && price?.infant === 0 ? (
									<>별도문의</>
								) : (
									<ItemPrice>
										<em>{format.koKRFormatNumber(price.infant)}</em> 원
									</ItemPrice>
								)}
							</div>
							<CountingButton
								increase={(count: number) => {
									setInfant(count);
									resetCoupon();
								}}
								decrease={(count: number) => {
									setInfant(count);
									resetCoupon();
								}}
								value={infant}
							/>
						</InfoListItem>
					</InfoList>
					<TotalWrap>
						<button onClick={openCoupon}>쿠폰 적용</button>
						<ItemPrice>
							<em>{format.koKRFormatNumber(totalPrice)}</em> 원
						</ItemPrice>
					</TotalWrap>
				</PartnerLayerWrap>
			)}
			<FixedBtnBlock>
				<InquiryButton text="문의" onClick={hanldeQna} />
				<ChangeDepartureButton
					types="confirm"
					text="출발일 변경"
					onClick={() => {
						changeDepartureDay();
						taggingClickChange();
					}}
				/>
				<ReserveButton
					types={reserveText[0]}
					text={reserveText[1]}
					isDisabled={reserveText[0] === "disabled"}
					waiting={reserveText[1] === "대기예약"}
					onClick={async () => {
						taggingClickReserve();
						await handleReserve();
					}}
				/>
			</FixedBtnBlock>
		</Wrap>
	);
};

const DetailSNB = (props: TDetailPackage) => (
	<ErrorBoundary logMsg="EventDetailSNB Error">
		<DetailSNBComponent {...props} />
	</ErrorBoundary>
);

export default memo(DetailSNB);
